import type {
  BillingStatusResponse,
  CreateBillingPortalSessionResponse,
  CreateCheckoutSessionResponse,
} from '@simplemon/lib';
import {
  BillingStatusResponseSchema,
  CreateBillingPortalSessionResponseSchema,
  CreateCheckoutSessionResponseSchema,
} from '@simplemon/lib';

type ResponseSchema<T> = {
  parse(input: unknown): T;
};

function safeErrorMessage(payload: unknown, fallbackError: string): string {
  if (!payload || typeof payload !== 'object' || !('error' in payload)) {
    return fallbackError;
  }

  const error = payload.error;
  const message =
    typeof error === 'string'
      ? error
      : error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string'
        ? error.message
        : undefined;

  const trimmedMessage = message?.trim();
  if (!trimmedMessage || trimmedMessage.includes('<') || trimmedMessage.includes('>')) {
    return fallbackError;
  }

  return trimmedMessage;
}

async function handleResponse<T>(
  response: Response,
  fallbackError: string,
  schema: ResponseSchema<T>,
): Promise<T> {
  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    throw new Error(fallbackError);
  }

  if (!response.ok) {
    throw new Error(safeErrorMessage(payload, fallbackError));
  }

  try {
    return schema.parse(payload);
  } catch {
    throw new Error(fallbackError);
  }
}

export const billingApi = {
  async getStatus(): Promise<BillingStatusResponse> {
    const response = await fetch('/api/billing/status', {
      credentials: 'include',
    });

    return handleResponse(response, 'Failed to fetch billing status', BillingStatusResponseSchema);
  },

  async createCheckoutSession(): Promise<CreateCheckoutSessionResponse> {
    const response = await fetch('/api/billing/checkout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    return handleResponse(
      response,
      'Failed to start checkout session',
      CreateCheckoutSessionResponseSchema,
    );
  },

  async createPortalSession(): Promise<CreateBillingPortalSessionResponse> {
    const response = await fetch('/api/billing/portal', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    return handleResponse(
      response,
      'Failed to create billing portal session',
      CreateBillingPortalSessionResponseSchema,
    );
  },
};
